import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	Input,
	NgModule,
	Pipe,
	PipeTransform,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ClientScreenDirectives } from '~app-client/core/directives';
import { ReachModalsService } from '~app-client/modals/services';
import { Implicit } from '~app-client/modals/types';
import { CarouselInstagramDotsModule } from '~app-ui/carousel/instagram';
import {
	CarouselContainerComponent,
	CarouselContainerModule,
	CarouselSlideModule,
	CarouselSlidesWrapperModule,
} from '~app-ui/carousel/with-slide';
import { SameWidthTextModule } from '~app-ui/same-width-text';

import { InfoModalCloseReason } from '../../types/info-modal-closed.type';
import { InfoModalKey, ModalData, SlideData } from '../../types/public_api';
import { InfoModalCdkModule } from '../modal-cdk/modal-cdk.component';
import { InfoModalSlideModule } from '../modal-slide/modal-slide.component';

@Pipe({
	standalone: false,
	name: 'slideTitle',
	pure: true,
})
export class SlideTitlePipe implements PipeTransform {
	transform(slide: SlideData, baseI18n: string): string {
		if (slide.videoId) {
			return `infoModals.general.tutorial_slide.title`;
		}

		return `infoModals.${baseI18n}.${slide.i18n}.title`;
	}
}

@Pipe({
	name: 'slideMessage',
	pure: true,
	standalone: false,
})
export class SlideMessagePipe implements PipeTransform {
	transform(slide: SlideData, baseI18n: string): string {
		if (slide.videoId) {
			return `infoModals.general.tutorial_slide.message`;
		}

		return `infoModals.${baseI18n}.${slide.i18n}.message`;
	}
}

@Pipe({
	name: 'slideFooter',
	pure: true,
	standalone: false,
})
export class SlideFooterPipe implements PipeTransform {
	transform(slide: SlideData, baseI18n: string): string {
		return `infoModals.${baseI18n}.${slide.i18n}.footer`;
	}
}

@Pipe({
	name: 'slideFooterFallback',
	pure: true,
	standalone: false,
})
export class SlideFooterFallbackPipe implements PipeTransform {
	transform(text: string, slide: SlideData, baseI18n: string, fallback: string): string {
		const footer = `infoModals.${baseI18n}.${slide.i18n}.footer`;
		if (text === footer) {
			return fallback;
		}
		return text;
	}
}

@Component({
	standalone: false,
	selector: 'info-modal-base',
	templateUrl: './modal-base.component.html',
	styleUrls: ['./modal-base.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{ provide: TRANSLOCO_SCOPE, useValue: { scope: 'info-modals', alias: 'infoModals' } },
	],
})
export class InfoModalBaseComponent implements AfterViewInit {
	@Input()
	public key!: InfoModalKey;

	@ViewChild('tpl')
	private tpl: TemplateRef<Implicit<void>>;

	@Input()
	public infoModalKey!: InfoModalKey;

	@Input()
	public set slides(slides: SlideData[]) {
		if (slides) {
			this.slides$$.next(slides);
			this.activeId$$.next(slides[0]?.i18n || '');
		}
	}

	@Input()
	public i18nName!: string;

	private readonly activeId$$ = new BehaviorSubject('');
	public readonly activeId$ = this.activeId$$.asObservable();

	private readonly slides$$ = new BehaviorSubject<ModalData['slides']>([]);
	public readonly slides$ = this.slides$$.asObservable();

	public readonly slideIdx$ = this.slides$$.pipe(
		switchMap((slides) => {
			return this.activeId$.pipe(
				map((activeId) => {
					return slides.findIndex((slide) => slide.i18n === activeId);
				})
			);
		})
	);

	public readonly ids$ = this.slides$.pipe(
		map((arr) => {
			return arr.map((s) => s.i18n);
		})
	);
	public readonly count$ = this.ids$.pipe(map((ids) => ids.length));
	public readonly showIndicator$ = this.count$.pipe(map((count) => count > 1));
	public readonly indicatorIndex$ = this.ids$.pipe(
		switchMap((ids) => {
			return this.activeId$.pipe(map((id) => ids.indexOf(id)));
		}),
		map((idx) => {
			return Math.max(0, idx);
		}),
		tap((idx) => {
			this._slidesSeen.add(idx);
		})
	);

	public readonly isFirstSlide$ = this.indicatorIndex$.pipe(
		map((idx) => {
			return this.ids$.pipe(map((ids) => idx === 0));
		})
	);

	public readonly isLastSlide$ = this.indicatorIndex$.pipe(
		switchMap((idx) => {
			return this.ids$.pipe(map((ids) => idx >= ids.length - 1));
		})
	);

	@Input()
	public hostZIndex?: number;

	public ids: string[] = [];

	public closing = false;

	private clickTimer = 0;
	private clickCooldownMs = 200;

	private readonly _slidesSeen = new Set<number>();

	constructor(private readonly modalService: ReachModalsService) {}

	ngAfterViewInit(): void {
		this.modalService.openModal(this.tpl, null, {
			closeOnOutsideClick: true,
			fullScreen: true,
			defaultContainerStyle: false,
			defaultHostStyle: false,
			hostZIndex: this.hostZIndex,
		});
	}

	public slideImageSrc(image: string): string {
		return `/assets/images/info-modals/${image}`;
	}

	public slideTitle(slide: string): string {
		return `infoModals.${this.i18nName}.${slide}.title`;
	}

	public slideMessage(slide: string): string {
		return `infoModals.${this.i18nName}.${slide}.message`;
	}

	public trackByI18n(_index: number, slide: SlideData): string {
		return slide.i18n;
	}

	public onActiveSlideChange(id: string): void {
		this.activeId$$.next(id);
	}

	public goNext(carousel: CarouselContainerComponent<string>): void {
		this.checkCooldown(() => {
			carousel?.goNext();
		});
	}

	public goBack(carousel: CarouselContainerComponent<string>): void {
		this.checkCooldown(() => {
			carousel?.goBack();
		});
	}

	public outsideClick(slide: SlideData): void {
		if (slide?.closeOnOutsideClick ?? true) {
			this.done('click-outside');
		}
	}

	public done(reason: InfoModalCloseReason): void {
		if (!this.closing) {
			this.checkCooldown(() => {
				this.closing = true;
				setTimeout(() => {
					this.modalService.closeModal(reason === 'done');
				}, 350);
			});
		}
	}

	private checkCooldown(cb: () => void): void {
		const now = performance.now();
		if (now - this.clickTimer > this.clickCooldownMs) {
			this.clickTimer = now;
			cb();
		}
	}
}

@NgModule({
	declarations: [
		InfoModalBaseComponent,
		SlideTitlePipe,
		SlideMessagePipe,
		SlideFooterPipe,
		SlideFooterFallbackPipe,
	],
	imports: [
		CommonModule,
		TranslocoModule,
		ClientScreenDirectives,

		SameWidthTextModule,

		InfoModalCdkModule,

		CarouselInstagramDotsModule,

		CarouselContainerModule,
		CarouselSlidesWrapperModule,
		CarouselSlideModule,

		InfoModalSlideModule,
	],
	exports: [InfoModalBaseComponent],
})
export class InfoModalBaseModule {}
